import { CommonModule } from '@angular/common';
import { Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { APP_STORE_CONFIG } from '../../config/app-stores.config';
import { PlatformService } from '../../services/platform.service';

const APP_STORE_BANNER_DISMISSED_KEY = 'app-store-banner-dismissed';

@Component({
  selector: 'app-store-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './app-store-banner.html',
  styleUrl: './app-store-banner.css',
})
export class AppStoreBanner {
  protected readonly storeConfig = APP_STORE_CONFIG;

  private readonly router = inject(Router);
  private readonly platformService = inject(PlatformService);
  private readonly localeId = inject(LOCALE_ID);

  private readonly currentUrl = signal(this.router.url.split('?')[0]);
  private readonly dismissed = signal(this.readDismissedState());

  protected readonly isVisible = computed(() => {
    const isAppRoute = this.currentUrl().startsWith('/app');
    const isHiddenPlatform = this.platformService.isIOSSafari();
    const isGerman = this.localeId?.toLowerCase().startsWith('de') ?? false;
    const showForPlatform =
      this.platformService.isAndroid() ||
      (this.platformService.isIOS() && !isHiddenPlatform);

    return (
      !isAppRoute &&
      !this.platformService.isStandalone() &&
      this.platformService.isMobile() &&
      showForPlatform &&
      !this.dismissed() &&
      isGerman
    );
  });

  protected readonly storeUrl = computed(() => {
    if (this.platformService.isAndroid()) {
      return this.storeConfig.androidAppUrl;
    }

    return this.storeConfig.iosAppDeUrl;
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        const navigationEvent = event as NavigationEnd;
        this.currentUrl.set(navigationEvent.urlAfterRedirects.split('?')[0]);
      });
  }

  protected dismiss(): void {
    this.dismissed.set(true);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(APP_STORE_BANNER_DISMISSED_KEY, '1');
    }
  }

  private readDismissedState(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    return localStorage.getItem(APP_STORE_BANNER_DISMISSED_KEY) === '1';
  }

  openStore(): void {
    window.open(this.storeUrl(), '_blank');
  }
}
